import type {
  BackupFile,
  ComicScene,
  CreativeProject,
  Language,
  NewProjectDraft,
} from "./domain";

export interface AssistanceService {
  createDraft(input: NewProjectDraft): Promise<string>;
}

export interface LanguageEditionService {
  createEdition(
    project: CreativeProject,
    target: Language,
    beginning: "empty" | "copy-source" | "paste",
    pastedText?: string,
  ): Promise<{ text: string; approach: "Empty edition" | "Copied source" | "Author-supplied translation" }>;
}

export interface StoryboardService {
  createScenes(project: CreativeProject): Promise<ComicScene[]>;
}

export interface TranscriptionService {
  simulatedTranscript(language: Language): Promise<string>;
}

export const localAssistance: AssistanceService = {
  async createDraft(input) {
    return createSimulatedDraft(input);
  },
};

export const localLanguageEditions: LanguageEditionService = {
  async createEdition(project, _target, beginning, pastedText = "") {
    if (beginning === "copy-source") {
      return { text: project.workingText, approach: "Copied source" };
    }
    if (beginning === "paste") {
      return { text: pastedText, approach: "Author-supplied translation" };
    }
    return { text: "", approach: "Empty edition" };
  },
};

function cleanSource(source: string) {
  return source
    .replace(/[ \t]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function sourceTerms(source: string) {
  return source
    .replace(/[“”"'.,!?;:()[\]—–-]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 3)
    .slice(0, 7)
    .join(" ");
}

function styleCue(input: NewProjectDraft) {
  const signals = [...input.tones, input.customTone].join(" ").toLowerCase();
  if (/funny|playful|whimsical/.test(signals)) return { opening: "Begin with lively movement and a light touch.", pace: "Use quick, buoyant sentences." };
  if (/dark|mysterious|suspense/.test(signals)) return { opening: "Begin close to uncertainty and reveal details gradually.", pace: "Use measured sentences with space for suspense." };
  if (/gentle|warm|moving|emotional/.test(signals)) return { opening: "Begin with a close, human detail.", pace: "Use an unhurried and attentive rhythm." };
  if (/serious|inspirational/.test(signals)) return { opening: "Begin with the central subject stated clearly.", pace: "Build carefully from context toward consequence." };
  const customShift = input.customTone.trim().length % 2 === 0;
  return customShift
    ? { opening: "Begin with one concrete detail from the source.", pace: "Move steadily from detail to meaning." }
    : { opening: "Begin with the source's strongest image or statement.", pace: "Alternate concise statements with reflective detail." };
}

function feelingCue(input: NewProjectDraft) {
  const signals = [...input.feelings, input.customFeeling, input.emotionalDirection].join(" ").toLowerCase();
  if (/comfort|safe|reassur|warm/.test(signals)) return "Resolve with safety, connection, or reassurance already supported by the source.";
  if (/hope|inspir|optim/.test(signals)) return "End by opening a credible possibility for change.";
  if (/fright|suspense|fear/.test(signals)) return "Let tension rise without adding harm that the author did not describe.";
  if (/sad|moved|nostalg/.test(signals)) return "Leave space for reflection and the emotional weight of what was supplied.";
  if (/curious|surpris|excite/.test(signals)) return "End on a discovery or question grounded in the supplied material.";
  return "Let the ending follow naturally from the supplied idea.";
}

function preservationCue(input: NewProjectDraft) {
  const lines = [];
  if (input.preserveWording) lines.push("Retain distinctive wording from the source wherever it remains clear.");
  if (input.preserveCulture) lines.push("Keep names, cultural words, and expressions exactly as supplied.");
  return lines.join(" ");
}

function structureFor(input: NewProjectDraft, source: string, terms: string) {
  const style = styleCue(input);
  const feeling = feelingCue(input);
  const preservation = preservationCue(input);
  const audience = input.audience && input.audience !== "Not sure yet"
    ? `Keep explanations suitable for ${input.audience.toLowerCase()}.`
    : "Keep the language accessible while the audience is still undecided.";
  const direction = input.emotionalDirection.trim()
    ? `Use the author's direction to control pacing and emphasis: ${input.emotionalDirection.trim()}`
    : feeling;

  if (input.originalLanguage === "si-LK" || /[\u0D80-\u0DFF]/u.test(source)) {
    return `${source}\n\n— Planning outline only —\nAutomatic Sinhala drafting is not available in this early version. The Sinhala source above remains unchanged.\n\n• Centre the plan on: ${terms || "the supplied Sinhala contribution"}.\n• ${style.opening}\n• ${direction}\n• ${preservation || "Preserve the author's Sinhala wording and cultural expressions."}`;
  }

  if (input.workType === "Non-fiction") {
    return `Introduction\n${source}\n\nMain points\n• Establish the supplied subject: ${terms || source}.\n• Organise only the facts, examples, and claims already present in the source.\n• ${style.pace}\n\nConclusion\n${direction} ${audience} ${preservation}`.trim();
  }
  if (input.workType === "Children's story") {
    return `Beginning\n${source}\n${style.opening}\n\nWhat happens next\nKeep ${terms || "the people and events in the idea"} at the centre. Develop only actions suggested by the author, using clear, age-appropriate sentences. ${style.pace}\n\nSafe ending\n${direction} ${audience} ${preservation}`.trim();
  }
  if (input.workType === "Fiction") {
    return `Opening\n${source}\n${style.opening}\n\nDevelopment\nFollow ${terms || "the supplied characters and events"} as the situation changes. ${style.pace}\n\nTurning point\nBring the supplied challenge or question into focus without inventing an unrelated event.\n\nEnding\n${direction} ${audience} ${preservation}`.trim();
  }
  return `Source\n${source}\n\nPossible shape\nBegin with ${terms || "the strongest supplied detail"}. ${style.opening} Arrange the existing people, subject, and events into a clear sequence.\n\nNext direction\n${direction} ${audience} ${preservation}`.trim();
}

function createSimulatedDraft(input: NewProjectDraft) {
  const source = cleanSource(input.idea);
  if (input.assistanceLevel === "Keep my words") return source;
  const terms = sourceTerms(source);
  if (input.originalLanguage === "si-LK" || /[\u0D80-\u0DFF]/u.test(source)) {
    return structureFor(input, source, terms);
  }
  if (input.assistanceLevel === "Help me improve it") {
    const sentences = source
      .replace(/\s+([,.!?])/g, "$1")
      .replace(/([.!?])([A-Za-z])/g, "$1 $2");
    const direction = input.emotionalDirection.trim()
      ? `Pacing follows this author-supplied direction: ${input.emotionalDirection.trim()}`
      : feelingCue(input);
    return `${sentences}\n\nEditing note\nThe sequence and meaning are unchanged. ${styleCue(input).pace} ${direction} ${preservationCue(input)}`.trim();
  }
  if (input.assistanceLevel === "Develop my ideas") {
    return `${source}\n\nDevelopment path\nReturn to ${terms || "the supplied subject"} and add concrete detail already implied by the source. ${styleCue(input).opening} ${styleCue(input).pace}\n\nWhat to deepen\nClarify what changes, why it matters to ${input.audience || "the intended reader"}, and how the supplied details connect. ${feelingCue(input)} ${preservationCue(input)}`.trim();
  }
  if (input.assistanceLevel === "Build it with me") {
    const answers = input.collaborationAnswers;
    return `${source}\n\nCo-created plan\nAt the centre: ${answers.centre || terms || "Decide together"}.\nWhat happens or changes: ${answers.change || "Add the next change together"}.\nMain challenge, question, or message: ${answers.challenge || "Decide together"}.\nHow it ends: ${answers.ending || feelingCue(input)}\nKeep exactly: ${answers.preserveExactly || preservationCue(input) || "The author's original contribution"}.\n\nFirst shaped section\n${structureFor(input, source, terms)}`;
  }
  return structureFor(input, source, terms);
}

export const simulatedStoryboard: StoryboardService = {
  async createScenes(project) {
    return [
      {
        id: crypto.randomUUID(),
        title: "The idea takes shape",
        setting: "A familiar place transformed by a small discovery",
        emotion: project.feelings[0] || "Wonder",
        panels: [
          {
            id: crypto.randomUUID(),
            visual: `A wide opening view inspired by “${project.title}”.`,
            narration: "A quiet moment becomes the beginning of a story.",
          },
          {
            id: crypto.randomUUID(),
            visual: "The central character notices a meaningful detail nearby.",
            narration: "Something familiar now feels different.",
            speaker: "Narrator",
            dialogue: "Perhaps this is where I should begin.",
          },
        ],
      },
    ];
  },
};

export const simulatedTranscription: TranscriptionService = {
  async simulatedTranscript(language) {
    if (language === "si-LK") {
      return "මගේ කතාව පටන් ගන්නේ පැරණි වත්තකින්. මෙය සංස්කරණය කළ හැකි ආදර්ශ වචන පෙළකි.";
    }
    return "My story begins in an old garden, where a small memory changes how a family sees its home.";
  },
};

export function createBackup(projects: CreativeProject[], unfinishedWork?: NewProjectDraft): BackupFile {
  return {
    format: "author-studio-backup",
    version: 2,
    exportedAt: new Date().toISOString(),
    projects,
    ...(unfinishedWork ? { unfinishedWork } : {}),
  };
}
